import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, HStack, Spinner, VStack } from "native-base";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { KeyboardAvoidingView } from "react-native";
import { TextInputMask } from "react-native-masked-text";

import FormButton from "@/components/atoms/FormButton";
import TextInput from "@/components/atoms/TextInput";
import FormField from "@/components/molecules/FormField";
import Map from "@/components/molecules/Map";

import { NewAddressFormData, schema } from "../schema";
import { NewAddresFormProps } from "../types";

const NewAddresForm = ({
  address,
  location,
  isSubmiting,
  isFetchingAddress,
  onCancel,
  onCepChange,
  onSubmit,
  onNumberChange,
}: NewAddresFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<NewAddressFormData>({
    resolver: yupResolver(schema),
  });

  const [cep, street, number, neighborhood] = watch(["cep", "street", "number", "neighborhood"]);

  useEffect(() => {
    if (address?.street) {
      setValue("street", address.street);
    }
    if (address?.neighborhood) {
      setValue("neighborhood", address.neighborhood);
    }
  }, [address]);

  return (
    <KeyboardAvoidingView>
      <VStack>
        <HStack w={isFetchingAddress ? "90%" : "100%"}>
          <FormField
            control={control}
            name="cep"
            label="Cep"
            required
            error={errors.cep}
            render={(value, onChange) => (
              <TextInputMask
                value={value}
                onChangeText={onChange}
                customTextInput={TextInput}
                onBlur={() => onCepChange(cep)}
                type={"zip-code"}
                placeholder="Cep"
              />
            )}
          />
          {isFetchingAddress && <Spinner />}
        </HStack>
        <FormField
          control={control}
          name="street"
          label="Rua"
          required
          error={errors.street}
          render={(value, onChange) => (
            <TextInput value={value} onChangeText={onChange} placeholder="Rua" />
          )}
        />
        <FormField
          control={control}
          name="neighborhood"
          label="Bairro"
          required
          error={errors.neighborhood}
          render={(value, onChange) => (
            <TextInput value={value} onChangeText={onChange} placeholder="Bairro" />
          )}
        />
        <HStack w="1/2" space={2}>
          <FormField
            control={control}
            name="number"
            label="Número"
            error={errors.number}
            render={(value, onChange) => (
              <TextInput
                value={value}
                onChangeText={onChange}
                placeholder="Número"
                onBlur={() =>
                  onNumberChange({
                    street,
                    number,
                    neighborhood,
                  })
                }
              />
            )}
          />
          <FormField
            control={control}
            name="completion"
            label="Complemento"
            required
            error={errors.completion}
            render={(value, onChange) => (
              <TextInput value={value} onChangeText={onChange} placeholder="Complemento" />
            )}
          />
        </HStack>
        <FormField
          control={control}
          name="reference"
          label="Referência"
          error={errors.reference}
          render={(value, onChange) => (
            <TextInput value={value} onChangeText={onChange} placeholder="Referência" />
          )}
        />
        {!!location && !!street && (
          <>
            <Box rounded="lg" shadow="6" mt="4" bgColor="white.50">
              <Map
                w="full"
                h="120px"
                region={{
                  ...location,
                  latitudeDelta: 0.009,
                  longitudeDelta: 0.009,
                }}
                coordinate={location}
              />
            </Box>
            <Divider mt="4" />
          </>
        )}
        <FormButton mt="10" isLoading={isSubmiting} onPress={handleSubmit(onSubmit)}>
          Cadastrar
        </FormButton>
        <FormButton
          colorScheme="white"
          textColor="primary.700"
          my="4"
          shadow="2"
          onPress={() => onCancel()}>
          Cancelar
        </FormButton>
      </VStack>
    </KeyboardAvoidingView>
  );
};

export default NewAddresForm;
